import React, { useState } from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle, 
  ChevronRight, 
  TrendingUp, 
  Users, 
  Cpu, 
  Calendar, 
  Award, 
  BookOpen, 
  ShieldCheck, 
  Sparkles,
  ExternalLink,
  MessageCircle,
  Play
} from "lucide-react";
import { motion } from "framer-motion";
import { EditorialMarquee } from "../components/EditorialMarquee";

export default function Home() {
  // Booking page link as provided by the user
  const bookingUrl = "https://academy.suhasinibasa.com/web/checkout/6a8eb2e3570da12b73781f39";

  // Pain Points before-and-after interactive state
  const [activePainPoint, setActivePainPoint] = useState(0);
  const painPoints = [
    {
      title: "Invisible Contribution",
      before: "You do excellent work, meet deadlines, and go the extra mile, but your contribution is invisible to senior leadership.",
      after: "Visibility strategies ensure decision-makers see your value and proactively seek you out.",
      testId: "pain-point-card-0"
    },
    {
      title: "Order-Taker Trap",
      before: "You are constantly executing other people's requests and templates rather than influencing strategic decisions.",
      after: "Transition to a trusted advisor who shapes the roadmap, commands respect, and influences without formal authority.",
      testId: "pain-point-card-1"
    },
    {
      title: "Invisible to Promoters",
      before: "You repeatedly take on more responsibility, but you are overlooked for promotions because you struggle to communicate achievements.",
      after: "Master the art of high-impact professional positioning and present evidence of impact so promotions become inevitable.",
      testId: "pain-point-card-2"
    },
    {
      title: "Stakeholder Friction",
      before: "You struggle to handle pushbacks on unrealistic scopes and deadlines from demanding stakeholders.",
      after: "Develop solid executive communication skills to negotiate terms with absolute authority and tact.",
      testId: "pain-point-card-3"
    },
    {
      title: "Stagnating Career Pace",
      before: "You've got 10+ years of IT experience, yet your salary and role feel stuck while peers move ahead.",
      after: "Leverage advanced career-growth frameworks that skip theory and target real corporate leaps.",
      testId: "pain-point-card-4"
    }
  ];

  // Six-step Career Transformation Framework
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      num: "01",
      name: "Confidence",
      desc: "Shatter the imposter syndrome. Build an authoritative mindset to speak up in critical leadership forums without hesitation.",
      metric: "100% confidence ownership"
    },
    {
      num: "02",
      name: "Communication",
      desc: "Move from technical details to business value. Speak the language of senior executives and translate execution into high-level strategic impact.",
      metric: "Clarity-first executive updates"
    },
    {
      num: "03",
      name: "Influence",
      desc: "Learn to steer decisions and guide complex stakeholders without relying on formal hierarchical authority.",
      metric: "Trusted advisor positioning"
    },
    {
      num: "04",
      name: "Visibility",
      desc: "Position your work where it matters. Implement practical visibility engines so key decision-makers naturally see your business contribution.",
      metric: "Proactive leadership recognition"
    },
    {
      num: "05",
      name: "Leadership",
      desc: "Adopt an ownership mindset. Transition from executing tasks to orchestrating value, managing risks, and defining success.",
      metric: "Strategic partnership model"
    },
    {
      num: "06",
      name: "Career Growth",
      desc: "Prepare for promotion and elite opportunities. Navigate advanced conversations with clarity and confidence.",
      metric: "Sustained promotional momentum"
    }
  ];

  // Animation constants for staggered reveals
  const fadeUp = {
    hidden: { opacity: 0, y: 35 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
  };

  const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
  };

  return (
    <div className="relative bg-obsidian text-slate-100 min-h-screen">
      {/* Noise overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-16 lg:py-32 px-4 sm:px-6 lg:px-12 overflow-hidden border-b border-white/5">
        {/* Animated grid/circle in background */}
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gold/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-[300px] h-[300px] bg-navy/40 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">
          
          {/* Hero Content */}
          <div className="lg:col-span-7 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-none font-mono text-xs text-gold uppercase tracking-[0.2em] font-semibold">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Suhasini Basa • Professional Mentorship</span>
            </div>

            <div className="space-y-4">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-white">
                You’re doing the work. <br />
                <span className="text-gold">Now get the recognition</span> you deserve.
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 font-normal max-w-2xl leading-relaxed">
                I help experienced IT Business Analysts and Project Managers build the confidence, visibility and influence they need to become recognized as strategic leaders and drive career growth.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/programs"
                data-testid="hero-explore-framework-button"
                className="bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest text-center transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 active:scale-95 flex items-center justify-center gap-2"
              >
                Explore Programs
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-start-assessment-button"
                className="bg-transparent hover:bg-white/5 text-white border border-white/20 hover:border-white px-8 py-4 font-display font-bold text-sm uppercase tracking-widest text-center transition-all duration-300 flex items-center justify-center gap-2"
              >
                Book a 1:1 Consultation
                <ExternalLink className="w-4 h-4 text-gold" />
              </a>
            </div>

            {/* Quick Metrics Banner */}
            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/5 font-mono">
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight">16+</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">Years IT Experience</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-gold tracking-tight">6-Step</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">Transform Framework</p>
              </div>
              <div>
                <p className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Elite</p>
                <p className="text-xs uppercase tracking-wider text-slate-500 mt-1">BA/PM Community</p>
              </div>
            </div>
          </div>

          {/* Hero Portrait Visual */}
          <div className="lg:col-span-5 relative flex justify-center">
            <div className="relative group max-w-sm sm:max-w-md w-full">
              <div className="absolute -inset-2 bg-gradient-to-r from-gold to-navy rounded-none blur opacity-25 group-hover:opacity-40 transition duration-1000" />
              
              {/* Image Border frame */}
              <div className="relative border border-white/10 bg-navy p-3 overflow-hidden shadow-2xl">
                <img 
                  src="/images/suhasini-basa-portrait.webp" 
                  alt="Suhasini Basa - Executive Career Transformation Coach"
                  className="w-full h-[380px] sm:h-[480px] object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <div className="absolute bottom-6 left-6 right-6 glassmorphism p-4 border border-white/10">
                  <p className="text-xs uppercase tracking-widest text-gold font-mono font-bold">Strategic Presence</p>
                  <p className="text-sm font-medium text-white mt-1">&quot;Technical execution gets you the role. Influence and visibility determine how far you go.&quot;</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* slow marquee banner */}
      <EditorialMarquee />

      {/* Pain Points / Identification Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">THE REALITY OF THE CORPORATE LADDER</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-snug">
              You may be good at your job. But being good at your job isn’t always enough.
            </h2>
            <p className="text-slate-400 text-base sm:text-lg leading-relaxed">
              Many seasoned professionals work relentlessly, hoping execution alone will trigger growth. In reality, leadership recognition is triggered by something entirely different. Click below to inspect your core bottleneck.
            </p>
          </div>

          {/* Interactive Bento Pain Points Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            {/* Cards navigation panel */}
            <div className="lg:col-span-5 flex flex-col space-y-3">
              {painPoints.map((point, index) => (
                <button
                  key={index}
                  data-testid={`pain-point-tab-${index}`}
                  onClick={() => setActivePainPoint(index)}
                  className={`w-full text-left p-5 transition-all duration-300 border font-display flex items-center justify-between ${
                    activePainPoint === index 
                      ? "bg-navy border-gold text-white pl-7" 
                      : "bg-white/[0.02] border-white/5 text-slate-400 hover:bg-white/[0.04] hover:border-white/10"
                  }`}
                >
                  <span className="font-semibold text-base sm:text-lg">{point.title}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${activePainPoint === index ? "rotate-90 text-gold" : "text-slate-500"}`} />
                </button>
              ))}
            </div>

            {/* Live Insight Display */}
            <div className="lg:col-span-7 bg-navy border border-white/10 p-8 sm:p-12 relative flex flex-col justify-between overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gold/5 rounded-full blur-[80px]" />
              
              <div className="relative space-y-8 z-10">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-gold font-bold">
                  CARD 0{activePainPoint + 1} // STRUCTURAL ANALYSIS
                </span>
                
                <div className="space-y-4">
                  <p className="text-xs uppercase tracking-widest text-red-400 font-mono">The Chronic Problem:</p>
                  <p className="text-lg sm:text-xl text-slate-300 italic font-serif">
                    &quot;{painPoints[activePainPoint].before}&quot;
                  </p>
                </div>

                <div className="space-y-4 pt-4 border-t border-white/10">
                  <p className="text-xs uppercase tracking-widest text-emerald-400 font-mono">The Strategic Shift:</p>
                  <p className="text-base sm:text-lg text-slate-100 font-normal leading-relaxed">
                    {painPoints[activePainPoint].after}
                  </p>
                </div>
              </div>

              <div className="relative mt-8 pt-6 border-t border-white/5 flex items-center justify-between font-mono text-xs text-slate-500 z-10">
                <span>CAREER INFLUENCER HUB</span>
                <span>CODE: SHIFT_0{activePainPoint + 1}</span>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-base sm:text-lg text-slate-300 font-medium">
              The solution isn&apos;t always to work harder. Sometimes, you need to become <span className="text-gold">more visible, influential and strategic</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Manifesto Principles Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-navy relative border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-2">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">OUR CORE PHILOSOPHY</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              The Rules of Professional Leverage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Chapter 1 */}
            <div className="bg-obsidian border border-white/5 p-8 relative overflow-hidden group hover:border-gold/30 transition-all duration-300 flex flex-col justify-between min-h-[300px]">
              <span className="absolute right-4 bottom-2 font-mono text-8xl font-bold text-white/[0.02] select-none group-hover:text-gold/[0.04] transition-all duration-300">
                01
              </span>
              <div className="space-y-4 relative z-10">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">Chapter I</span>
                <h3 className="font-display text-xl font-bold text-white">Evidence Beats Effort</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Doing good work is important. But career growth also requires you to clearly demonstrate and communicate the strategic business impact of that work.
                </p>
              </div>
              <span className="text-xs font-mono text-slate-500 pt-6 border-t border-white/5">PRINCIPLE // EVIDENCE</span>
            </div>

            {/* Chapter 2 */}
            <div className="bg-obsidian border border-white/5 p-8 relative overflow-hidden group hover:border-gold/30 transition-all duration-300 flex flex-col justify-between min-h-[300px]">
              <span className="absolute right-4 bottom-2 font-mono text-8xl font-bold text-white/[0.02] select-none group-hover:text-gold/[0.04] transition-all duration-300">
                02
              </span>
              <div className="space-y-4 relative z-10">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">Chapter II</span>
                <h3 className="font-display text-xl font-bold text-white">Visibility Is a Skill</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Being extremely capable is not enough if the executives making critical promotion and career decisions are completely blind to your contribution.
                </p>
              </div>
              <span className="text-xs font-mono text-slate-500 pt-6 border-t border-white/5">PRINCIPLE // ENGINE</span>
            </div>

            {/* Chapter 3 */}
            <div className="bg-obsidian border border-white/5 p-8 relative overflow-hidden group hover:border-gold/30 transition-all duration-300 flex flex-col justify-between min-h-[300px]">
              <span className="absolute right-4 bottom-2 font-mono text-8xl font-bold text-white/[0.02] select-none group-hover:text-gold/[0.04] transition-all duration-300">
                03
              </span>
              <div className="space-y-4 relative z-10">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">Chapter III</span>
                <h3 className="font-display text-xl font-bold text-white">Don’t Wait for Permission</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Career growth requires complete ownership. Active professionals shape how they communicate value, handle stakeholders, and position themselves.
                </p>
              </div>
              <span className="text-xs font-mono text-slate-500 pt-6 border-t border-white/5">PRINCIPLE // PROACTIVE</span>
            </div>
          </div>
        </div>
      </section>

      {/* Six Step Transformation Framework */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">THE CORE SYSTEM</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              From Doing the Work to Owning Your Career
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              Suhasini&apos;s signature professional framework strengthens the six critical pillars of career acceleration. Click through the phases of transformation.
            </p>
          </div>

          {/* Stepper Display */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            
            {/* Horizontal timeline selector */}
            <div className="lg:col-span-4 flex flex-col space-y-2">
              {steps.map((step, idx) => (
                <button
                  key={idx}
                  data-testid={`framework-step-tab-${idx}`}
                  onClick={() => setActiveStep(idx)}
                  className={`w-full text-left p-4 sm:p-5 flex items-center justify-between border transition-all duration-200 ${
                    activeStep === idx 
                      ? "bg-navy border-gold text-white" 
                      : "bg-white/[0.01] border-white/5 text-slate-400 hover:bg-white/[0.03]"
                  }`}
                >
                  <div className="flex items-center space-x-4">
                    <span className="font-mono text-xs text-gold font-bold">{step.num}</span>
                    <span className="font-display font-semibold text-base sm:text-lg">{step.name}</span>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${activeStep === idx ? "text-gold" : "text-slate-600"}`} />
                </button>
              ))}
            </div>

            {/* Stepper Details Box */}
            <div className="lg:col-span-8 bg-navy border border-white/10 p-8 sm:p-12 relative min-h-[320px] flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-gold font-bold">
                    PHASE {steps[activeStep].num} // ACCELERATOR
                  </span>
                  <span className="px-3 py-1 bg-white/5 border border-white/10 text-white font-mono text-xs">
                    ACTIVE MODULE
                  </span>
                </div>
                
                <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">
                  {steps[activeStep].name} Transformation
                </h3>
                
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                  {steps[activeStep].desc}
                </p>
              </div>

              <div className="mt-8 pt-6 border-t border-white/15 flex flex-col sm:flex-row sm:items-center justify-between gap-4 font-mono text-sm">
                <span className="text-slate-400">Target Outcome:</span>
                <span className="text-gold font-bold uppercase tracking-wider">{steps[activeStep].metric}</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Practical Transformation Areas (How Suhasini Helps) */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-navy relative border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">COACHING OUTCOMES</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Practical career transformation for real-world BA &amp; PM challenges
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                title: "Career Positioning",
                desc: "Define your elite professional value proposition. Optimize how you present your experience so you qualify for tier-1 roles."
              },
              {
                title: "Radical Visibility",
                desc: "Establish systemic feedback structures to highlight your contributions directly to stakeholders without feeling salesy."
              },
              {
                title: "Influence Without Authority",
                desc: "Gain deep communication scripts and mental models to manage competing priorities and guide stakeholders."
              },
              {
                title: "Stakeholder Management",
                desc: "Navigate complex pushbacks, scope creep, and executive alignment challenges with calm, proven negotiation tactics."
              },
              {
                title: "Executive Communication",
                desc: "Deliver strategic business risk assessments and roadmap recommendations in minutes with professional elegance."
              },
              {
                title: "Promotion Strategy",
                desc: "Prepare for hard alignment talks, display organizational value, and justify double-digit salary leaps."
              },
              {
                title: "BA/PM Interview Prep",
                desc: "Master scenario-based analytical and leadership questions asked during complex enterprise hiring rounds."
              },
              {
                title: "AI for BA/PM Professionals",
                desc: "Slay manual execution work. Master custom AI workflows to draft documentation, prepare analysis, and free up strategic time."
              }
            ].map((help, index) => (
              <div 
                key={index}
                className="bg-obsidian border border-white/5 p-6 hover:border-gold/20 hover:scale-[1.02] transition-all duration-300 flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="w-10 h-10 bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                    <CheckCircle className="w-5 h-5 text-gold" />
                  </div>
                  <h4 className="font-display text-lg font-semibold text-white">{help.title}</h4>
                  <p className="text-slate-400 text-sm leading-relaxed">{help.desc}</p>
                </div>
                <div className="pt-6 mt-6 border-t border-white/5 flex justify-between items-center text-xs font-mono text-slate-500">
                  <span>OUTCOME COMPONENT</span>
                  <span>0{index + 1}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/programs"
              data-testid="see-how-i-can-help-cta"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest transition-colors duration-200"
            >
              See How I Can Help
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Programs and Pricing Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">CAREER HUB LEVELS</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Choose the level of support that fits your career
            </h2>
            <p className="text-slate-400 text-base sm:text-lg">
              From immediate LinkedIn profile transformations to deep elite mastermind certification pathways, find the custom structure for your goals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            
            {/* L0 Fast Start */}
            <div className="bg-navy border border-white/5 p-6 flex flex-col justify-between hover:border-gold/30 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-wider font-bold">L0 // START BUNDLE</span>
                <h3 className="font-display text-xl font-bold text-white">Fast Start Bundle</h3>
                <p className="text-xs text-slate-400">Immediate, actionable strategic networking guidelines.</p>
                <div className="border-t border-white/5 pt-4 space-y-2 text-xs text-slate-300">
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Optimize LinkedIn Profile</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Recruiter outreach game-plan</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Custom prompt templates</p>
                </div>
              </div>
              <Link 
                to="/programs" 
                data-testid="explore-fast-start-button"
                className="mt-8 bg-white/5 hover:bg-white/10 text-white font-display font-bold text-xs uppercase tracking-wider py-3 text-center transition-colors duration-200"
              >
                Explore Fast Start
              </Link>
            </div>

            {/* L1 Silver */}
            <div className="bg-navy border border-white/5 p-6 flex flex-col justify-between hover:border-gold/30 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-wider font-bold">L1 // MEMBERSHIP</span>
                <h3 className="font-display text-xl font-bold text-white">Silver Membership</h3>
                <p className="text-xs text-slate-400">Foundational curriculum for structured growth.</p>
                <div className="border-t border-white/5 pt-4 space-y-2 text-xs text-slate-300">
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> 6-Step Core Curriculum</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> 1-1 Career Coaching Call</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Weekly Inner Circle Call</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> 90 Days Growth Challenge</p>
                </div>
              </div>
              <Link 
                to="/programs" 
                data-testid="explore-silver-button"
                className="mt-8 bg-white/5 hover:bg-white/10 text-white font-display font-bold text-xs uppercase tracking-wider py-3 text-center transition-colors duration-200"
              >
                Explore Silver
              </Link>
            </div>

            {/* L2 Gold - highlighted */}
            <div className="bg-navy border border-white/5 p-6 flex flex-col justify-between relative shadow-xl hover:border-gold/30 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-wider font-bold">L2 // MASTERMIND</span>
                <h3 className="font-display text-xl font-bold text-white">Gold Membership</h3>
                <p className="text-xs text-slate-300">Advanced professional transformation and influence.</p>
                <div className="border-t border-white/10 pt-4 space-y-2 text-xs text-slate-200">
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Weekly mastermind cohorts</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> Executive speech alignment</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> 50+ AI Prompts Toolkit for Project Execution</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-gold" /> 90 Days Growth Challenge</p>
                </div>
              </div>
              <Link 
                to="/programs" 
                data-testid="explore-gold-button"
                className="mt-8 bg-gold hover:bg-gold-hover text-obsidian font-display font-bold text-xs uppercase tracking-wider py-3 text-center transition-colors duration-200"
              >
                Explore Gold
              </Link>
            </div>

          </div>
        </div>
      </section>

      {/* 1:1 Coaching Showcase */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-navy relative border-y border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">BESPOKE PARTNERSHIP</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Sometimes you don’t need another course. You need a strategy built around YOU.
            </h2>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              1:1 Coaching offers high-level personalization. I analyze your organization&apos;s specific dynamics, dissect your stakeholder communication history, and build tactical response parameters that fit your environment perfectly.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-slate-300 text-sm font-mono pt-4">
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-gold" /> Custom Promotion Positioning</div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-gold" /> Real-time Stakeholder Scripting</div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-gold" /> Confident Negotiation Drills</div>
              <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-gold" /> Personalized Visibility Plan</div>
            </div>

            <div className="pt-6">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-book-coaching-button"
                className="bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2"
              >
                Book a 1:1 Consultation
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="border border-white/10 bg-obsidian p-4 shadow-2xl relative">
              <img 
                src="https://images.unsplash.com/photo-1573497620053-ea5300f94f21?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Suhasini Basa conducting 1:1 Executive Mentorship session" 
                className="w-full h-[320px] object-cover grayscale contrast-125"
              />
              <div className="absolute top-8 left-8 bg-gold text-obsidian font-mono text-[9px] uppercase tracking-widest font-bold px-3 py-1">
                LIMITED SEATS AVAILABLE
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* About Suhasini Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          <div className="lg:col-span-5">
            <div className="relative border border-white/10 p-3 bg-navy">
              <img 
                src="/images/suhasini-basa-portrait.webp" 
                alt="Suhasini Basa - Career Coach Profile" 
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6 text-left">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">MEET YOUR COACH</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Learn from someone who has been inside the room.
            </h2>
            
            <p className="text-slate-300 text-base sm:text-lg italic font-serif leading-relaxed">
              &quot;I spent years working inside complex technology and financial-services environments, moving through roles in software development, business analysis and project management. That experience taught me something important: technical and delivery skills alone do not determine career growth.&quot;
            </p>

            <p className="text-slate-400 text-sm leading-relaxed">
              With over <strong>16 years of hands-on experience</strong> spanning Software Development, Business Analysis, Project Management, and Capital Markets, I know exactly what parameters senior executives judge when appointing leaders. I ground my entire mentorship system in actionable execution templates, not high-level motivational theory.
            </p>



            <div className="pt-4">
              <Link
                to="/about"
                data-testid="about-learn-more-button"
                className="inline-flex items-center gap-2 bg-transparent hover:bg-white/5 text-white border border-white/20 hover:border-white px-8 py-4 font-display font-bold text-sm uppercase tracking-widest text-center transition-colors duration-200"
              >
                Learn More About Suhasini
              </Link>
            </div>
          </div>

        </div>
      </section>

      {/* Is This For You Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-navy relative border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">COMPATIBILITY PARAMETERS</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Is this for you?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Experienced Business Analysts aiming for executive levels.",
              "Senior Project Managers striving for high-value strategic authority.",
              "Experienced IT professionals with 5 to 16 years of experience.",
              "Delivery professionals transitioning to senior BA or PM mandates.",
              "High-potential individual contributors ready to direct stakeholders.",
              "Ambitious professionals preparing for deep salary negotiation."
            ].map((target, idx) => (
              <div key={idx} className="bg-obsidian border border-white/5 p-6 flex items-start space-x-4">
                <CheckCircle className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <p className="text-slate-300 text-sm sm:text-base">{target}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free Resources Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">CAREER UTILITIES</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Practical resources to help you grow your BA/PM career
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Career Visibility Checklist",
                desc: "Step-by-step organizational parameter tracker to guarantee your contributions reach team leaders."
              },
              {
                title: "Promotion Conversation Guide",
                desc: "Word-for-word scripts to align, pitch, and validate salary increases with absolute corporate diplomacy."
              },
              {
                title: "30-Day LinkedIn Visibility Plan",
                desc: "Daily action sequences to build modern technical branding authority without consuming hours."
              }
            ].map((res, index) => (
              <div key={index} className="bg-navy border border-white/5 p-6 flex flex-col justify-between hover:border-gold/20 transition-all duration-300">
                <div className="space-y-4">
                  <div className="w-10 h-10 bg-white/5 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-white">{res.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{res.desc}</p>
                </div>
                <Link
                  to="/free-resources"
                  data-testid={`free-resource-card-cta-${index}`}
                  className="mt-6 flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-gold hover:text-white transition-colors duration-200"
                >
                  <span>Get Free Resource</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Platforms Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-navy relative border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">DIGITAL ECOSYSTEM</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Learn with Suhasini
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* LinkedIn */}
            <div className="bg-obsidian border border-white/5 p-8 flex flex-col justify-between hover:border-gold/20 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">LINKEDIN HIGHLIGHTS</span>
                <h3 className="font-display text-lg font-bold text-white">Career Strategy &amp; Outreach</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Daily deep dives into IT service communication architectures, organization politics, and direct profile visibility.
                </p>
              </div>
              <a
                href="https://www.linkedin.com/in/suhasini-basa-pmp"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-follow-linkedin"
                className="mt-8 inline-flex items-center justify-between bg-white/5 hover:bg-white/10 text-white font-mono text-xs uppercase tracking-widest px-4 py-3"
              >
                <span>Follow on LinkedIn</span>
                <ExternalLink className="w-4 h-4 text-gold" />
              </a>
            </div>

            {/* Instagram */}
            <div className="bg-obsidian border border-white/5 p-8 flex flex-col justify-between hover:border-gold/20 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">INSTAGRAM MICRO-TIPS</span>
                <h3 className="font-display text-lg font-bold text-white">Short Action Guides</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Tactical scenario breakdowns for immediate implementation in meetings and stakeholder scope management.
                </p>
              </div>
              <a
                href="https://www.instagram.com/suhasini.basa?igsh=YzVpbmYwejZxMnJy"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-follow-instagram"
                className="mt-8 inline-flex items-center justify-between bg-white/5 hover:bg-white/10 text-white font-mono text-xs uppercase tracking-widest px-4 py-3"
              >
                <span>Follow on Instagram</span>
                <ExternalLink className="w-4 h-4 text-gold" />
              </a>
            </div>

            {/* Facebook */}
            <div className="bg-obsidian border border-white/5 p-8 flex flex-col justify-between hover:border-gold/20 transition-all duration-300">
              <div className="space-y-4">
                <span className="font-mono text-xs text-gold uppercase tracking-widest font-bold">FACEBOOK INSIGHTS</span>
                <h3 className="font-display text-lg font-bold text-white">Community &amp; Cohort updates</h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Join structural networking and receive the latest updates regarding upcoming free workshops and material releases.
                </p>
              </div>
              <a
                href="https://www.facebook.com/share/19AC77VpW8/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-follow-facebook"
                className="mt-8 inline-flex items-center justify-between bg-white/5 hover:bg-white/10 text-white font-mono text-xs uppercase tracking-widest px-4 py-3"
              >
                <span>Follow on Facebook</span>
                <ExternalLink className="w-4 h-4 text-gold" />
              </a>
            </div>

          </div>
        </div>
      </section>

      {/* Why Work With Suhasini Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-12 bg-obsidian relative border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl text-left mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">THE SUHASINI DIFFERENCE</span>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-white">
              Why partner with Suhasini?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "16 Years of Corporate Experience", body: "Direct experience executing soft-dev, business analysis, and project management inside tier-1 corporate systems." },
              { title: "BA + PM Core Perspective", body: "We map both deep technical analysis scope and overarching delivery management requirements smoothly." },
              { title: "Financial Services Expertise", body: "Grounding career methods in complex, high-stakes trade, treasury and capital-market business environments." },
              { title: "Practical Frameworks Only", body: "Zero motivational theories. Purely tactical scripts, checklist sequences, and organizational alignment parameters." },
              { title: "Career + Capability Boost", body: "Simultaneously maximize your technical expertise value and your public presentation authority." },
              { title: "AI-Aware Modern Career Playbook", body: "Deploy customized productivity workflows to free up 10+ hours a week for high-value visibility work." }
            ].map((item, idx) => (
              <div key={idx} className="bg-navy border border-white/5 p-6 space-y-3">
                <span className="font-mono text-xs text-gold font-bold">CRITERION 0{idx + 1}</span>
                <h3 className="font-display text-lg font-bold text-white">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final Closing Call-To-Action (CTA) */}
      <section className="py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-navy relative overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-[140px] pointer-events-none" />
        
        <div className="max-w-4xl mx-auto relative z-10 space-y-8">
          <span className="text-xs uppercase tracking-[0.3em] text-gold font-mono font-bold block">TAKE SYSTEMATIC ACTION</span>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            Your experience already has value. <br className="hidden sm:block" />
            Let&apos;s make sure your career reflects it.
          </h2>
          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">
            Build the confidence, communication, influence and visibility required to take ownership of your next high-value career transition.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link
              to="/start-here"
              data-testid="final-cta-start-here"
              className="bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest text-center transition-all duration-300"
            >
              Start Here
            </Link>
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-book-coaching"
              className="bg-transparent hover:bg-white/5 text-white border border-white/20 hover:border-white px-8 py-4 font-display font-bold text-sm uppercase tracking-widest text-center transition-all duration-300"
            >
              Book a 1:1 Consultation
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
